import axios from 'axios';
import { logout } from '../services/auth/login';

const axiosInstance = axios.create({
  baseURL: `https://${process.env.REACT_APP_API_URL}/api/`,
});

const refreshTokenInstance = axios.create({
  baseURL: `https://${process.env.REACT_APP_API_URL}/api/`,
});

/**
 * Token refresh queue management.
 *
 * When multiple requests fail simultaneously due to an expired token,
 * only one refresh request is sent. Other requests wait in a queue
 * and are retried once the new token is available.
 */
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

/**
 * Notifies all queued requests that a new token is available.
 * Executes their callbacks with the new token and clears the queue.
 */
const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Adds a request callback to the queue while waiting for token refresh.
 */
const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Request interceptor: attaches access token to outgoing requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem('accessToken') ||
      sessionStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handles 403 errors and refreshes token
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 403 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const refreshToken =
        localStorage.getItem('refreshToken') ||
        sessionStorage.getItem('refreshToken');

      if (!refreshToken) {
        logout();
        return Promise.reject(error);
      }

      // If a refresh is already in progress, wait for the new token
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const response = await refreshTokenInstance.post(
          'users/token/refresh-token',
          {
            refreshToken: refreshToken,
          },
        );

        if (response.status === 200) {
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken;

          // Update tokens in localStorage or sessionStorage
          if (localStorage.getItem('refreshToken')) {
            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
          } else {
            sessionStorage.setItem('accessToken', newAccessToken);
            sessionStorage.setItem('refreshToken', newRefreshToken);
          }

          isRefreshing = false;
          onRefreshed(newAccessToken);

          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshTokenError) {
        isRefreshing = false;
        refreshSubscribers = [];
        logout();
        return Promise.reject(refreshTokenError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
